import { Link } from "react-router-dom";
import { ArrowRight, Check, MessageCircle, MapPinned } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PhotoHero, Reveal, Img, ParallaxImg, Container, SectionTitle, Chip } from "@/components/common/Primitives";
import { useConcierge } from "@/components/concierge/ConciergeContext";
import { useLang } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { usePageMeta } from "@/lib/seo";
import { SIZES } from "@/lib/images";

/* Per-activity art direction: aspect for the lead photo + object positions */
const ART = {
  pool: { lead: "aspect-[4/3]", pos: ["50% 55%", "50% 40%", "50% 50%"] },
  peparen: { lead: "aspect-[4/3]", pos: ["50% 55%", "50% 60%", "50% 35%"] },
  kayak: { lead: "aspect-[4/3]", pos: ["50% 50%", "50% 55%"] },
  hike: { lead: "aspect-[4/3]", pos: ["50% 50%", "50% 45%"] },
  nature: { lead: "aspect-[4/3]", pos: ["50% 50%", "50% 55%"] },
  daytrips: { lead: "aspect-[4/3]", pos: ["50% 45%", "50% 50%"] },
};

const ActivitySection = ({ a, index, lang, tr }) => {
  const art = ART[a.id] || { lead: "aspect-[4/3]", pos: [] };
  const imgs = (a.images || []).slice(0, 3);
  const facts = a.facts?.[lang] || a.facts?.nn || [];
  const flip = index % 2 === 1;
  return (
    <section id={a.id} className="scroll-mt-28 pt-20 lg:pt-32" data-testid={`opplev-${a.id}`}>
      <Container className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center lg:gap-16">
        <Reveal className={`lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
          <div className={`grid gap-3 sm:gap-4 ${imgs.length > 1 ? "grid-cols-3" : "grid-cols-1"}`}>
            <Img src={imgs[0]} alt={tr(a.title)} className={`img-zoom ${imgs.length > 1 ? "col-span-2" : ""} ${art.lead}`} radius="rounded-[22px] sm:rounded-[26px]" sizes={SIZES.half} position={art.pos[0]} eager={index === 0} />
            {imgs.length > 1 && (
              <div className="flex flex-col gap-3 sm:gap-4">
                {imgs.slice(1, 3).map((src, i) => (
                  <Img key={src} src={src} alt="" className="img-zoom flex-1 min-h-[96px]" radius="rounded-[18px] sm:rounded-[22px]" sizes={SIZES.quarter} position={art.pos[i + 1]} />
                ))}
              </div>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.05} className={`lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
          <p className="text-sm font-medium text-muted-foreground">{tr(a.kicker)}</p>
          <h2 className="mt-2 text-[1.875rem] leading-[1.02] tracking-[-0.03em] text-foreground sm:text-[2.375rem]">{tr(a.title)}</h2>
          <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-muted-foreground sm:text-lg">{tr(a.text)}</p>
          {facts.length > 0 && (
            <ul className="mt-6 space-y-2.5" data-testid={`opplev-${a.id}-facts`}>
              {facts.map((f) => (
                <li key={f} className="flex items-start gap-3 text-[0.9375rem] text-foreground"><span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground"><Check className="h-3 w-3" strokeWidth={3} /></span>{f}</li>
              ))}
            </ul>
          )}
        </Reveal>
      </Container>
    </section>
  );
};

export default function Opplev() {
  const { lang, tx, tr } = useLang();
  const { data } = useContent();
  const { ask } = useConcierge();
  const activities = [...(data?.activities || [])].sort((a, b) => (a.sort || 0) - (b.sort || 0));
  usePageMeta(
    tx("Opplev", "Explore"),
    tx("Utandørsbassenget Sjøkanten Trivsel, badeøya Peparen, kajakk og sykkel, turar frå døra og dagsturar til Trolltunga, Preikestolen og Langfoss.", "The outdoor pool Sjøkanten Trivsel, Peparen bathing island, kayaks and bikes, hikes from the door and day trips to Trolltunga, Preikestolen and Langfoss.")
  );

  return (
    <div data-testid="opplev-page">
      <PhotoHero
        testid="opplev-hero"
        image="/images/peparen-summer.jpg"
        mobileImage="/images/peparen-bridge.jpg"
        position="50% 58%"
        mobilePosition="50% 55%"
        alt={tx("Badeøya Peparen ein sommardag – sandstrand, klart vatn og folk som badar", "Peparen bathing island on a summer day – sandy beach, clear water and people swimming")}
        kicker={tx("Ved fjorden og i fjellet", "By the fjord and in the mountains")}
        title={tx("Opplev Skånevik", "Explore Skånevik")}
        lead={tx("Bassenget ligg i hagen, badeøya fem minutt unna og fjellet rett bak bygda. Det meste kan du gjere til fots frå hotelldøra.", "The pool is in the garden, the bathing island five minutes away and the mountains right behind the village. Most of it starts on foot from the hotel door.")}
        caption={tx("Badeøya Peparen – fem minutt å gå frå hotellet", "Peparen bathing island – a five-minute walk from the hotel")}
        height="h-[72svh] min-h-[520px] max-h-[820px]"
      />

      {/* Jump nav */}
      {activities.length > 0 && (
        <Container className="mt-8 lg:mt-10">
          <div className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:px-0" data-testid="opplev-nav">
            {activities.map((a) => (
              <Chip key={a.id} as="a" href={`#${a.id}`} testid={`opplev-nav-${a.id}`}>{tr(a.title)}</Chip>
            ))}
          </div>
        </Container>
      )}

      {activities.map((a, i) => <ActivitySection key={a.id} a={a} index={i} lang={lang} tr={tr} />)}

      {/* Illustrated village map */}
      <section className="mt-20 bg-secondary/70 py-20 lg:mt-32 lg:py-32" data-testid="opplev-map-section">
        <Container className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center lg:gap-16">
          <Reveal className="lg:col-span-7">
            <figure className="overflow-hidden rounded-[24px] border border-border bg-card p-3 shadow-float sm:p-4" data-testid="opplev-illustrated-map">
              <Img src="/images/illustrated-village.jpg" alt={tx("Illustrert kart over Skånevik med hotellet, kaien, bassenget og Peparen", "Illustrated map of Skånevik with the hotel, the quay, the pool and Peparen")} className="aspect-[3/2]" radius="rounded-[16px]" sizes={SIZES.half} reveal={false} />
              <figcaption className="flex items-start gap-3 px-2 pb-1 pt-4 text-sm text-muted-foreground">
                <MapPinned className="mt-0.5 h-4 w-4 shrink-0 text-foreground" strokeWidth={1.75} aria-hidden />
                <span>{tx("Bygda teikna for oss: hotellet ved kaien, hagen med bassenget, brua over til Peparen og vegen opp mot fjellet. Hent papirutgåva i resepsjonen.", "The village drawn for us: the hotel by the quay, the garden with the pool, the bridge across to Peparen and the road up towards the mountains. Pick up the paper version at reception.")}</span>
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.05} className="lg:col-span-5">
            <SectionTitle size="lg" title={tx("Alt innanfor gangavstand", "Everything within walking distance")} />
            <dl className="mt-8 divide-y divide-border border-y border-border" data-testid="opplev-distances">
              {[
                [tx("Badeøya Peparen", "Peparen bathing island"), tx("5 min å gå", "5 min walk")],
                [tx("Daglegvarebutikk og bakeri", "Grocery shop and bakery"), tx("3 min å gå", "3 min walk")],
                [tx("Snøggbåtkaien", "Express boat quay"), "100 m"],
                [tx("Utgangspunkt for turstiane", "Trailheads"), tx("10–15 min å gå", "10–15 min walk")],
                [tx("Langfoss", "Langfoss waterfall"), tx("45 min med bil", "45 min by car")],
                [tx("Trolltunga (parkering Skjeggedal)", "Trolltunga (Skjeggedal car park)"), tx("om lag 2 t med bil", "about 2 h by car")],
              ].map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-6 py-3.5 text-[0.9375rem]">
                  <dt className="text-foreground">{k}</dt>
                  <dd className="shrink-0 font-semibold tabular text-foreground">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-xs text-muted-foreground">{tx("Avstandar er omtrentlege. Spør i resepsjonen om kart, rutetider og vêrmelding.", "Distances are approximate. Ask at reception for maps, timetables and the weather forecast.")}</p>
          </Reveal>
        </Container>
      </section>

      {/* Evening band (secondary, seasonal) */}
      <section className="relative h-[60svh] max-h-[680px] min-h-[420px] overflow-hidden bg-primary" data-testid="opplev-evening-band">
        <ParallaxImg src="/images/sunset-pink.jpg" mobileSrc="/images/family-sunset-beach.jpg" alt={tx("Kveldshimmel over Skånevikfjorden", "Evening sky over the Skånevik fjord")} className="absolute inset-0 h-full w-full" radius="rounded-none" position="50% 45%" mobilePosition="50% 50%" range={7} sizes={SIZES.full} />
        <div className="scrim-hero-soft pointer-events-none absolute inset-x-0 bottom-0 h-[70%]" aria-hidden />
        <Container className="relative flex h-full flex-col justify-end pb-10 lg:pb-14">
          <Reveal className="max-w-2xl">
            <p className="hero-shadow text-[0.9375rem] font-semibold text-white">{tx("Når dagen er over", "When the day is done")}</p>
            <h2 className="hero-shadow mt-3 max-w-[18ch] text-[2rem] font-semibold leading-[0.98] tracking-[-0.035em] text-white sm:text-[2.75rem]">{tx("Middag i restauranten, kaffi ved peisen", "Dinner in the restaurant, coffee by the fire")}</h2>
            <p className="hero-shadow mt-4 max-w-lg text-[1.0625rem] leading-relaxed text-white/90">{tx("Fjordterrassen har sol til seint på kvelden om sommaren. Om vinteren flyttar me inn i peisestova.", "The fjord terrace keeps the sun until late on summer evenings. In winter we move inside to the fireplace lounge.")}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild size="lg" variant="inverse" data-testid="opplev-dining-cta"><Link to="/mat">{tx("Sjå mat og menyar", "See food and menus")} <ArrowRight className="h-4 w-4" /></Link></Button>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32" data-testid="opplev-cta">
        <Container>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-[1.75rem] leading-[1.05] tracking-[-0.03em] text-foreground sm:text-[2.125rem]">{tx("Planlegg dagane dine med oss", "Plan your days with us")}</h2>
              <p className="mt-3 max-w-[52ch] text-base leading-relaxed text-muted-foreground sm:text-lg">{tx("Fjordverten veit når bassenget er ope, kva turar som passar i dag og korleis du kjem deg til Trolltunga.", "Fjordverten knows when the pool is open, which hikes suit today and how to get to Trolltunga.")}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button variant="soft" size="lg" onClick={() => ask(lang === "en" ? "What can we do in Skånevik over two days?" : "Kva kan me gjere i Skånevik på to dagar?")} data-testid="opplev-ask-cta"><MessageCircle className="h-4 w-4" /> {tx("Spør Fjordverten", "Ask Fjordverten")}</Button>
              <Button asChild size="lg" data-testid="opplev-book-cta"><Link to="/bestill">{tx("Sjå ledige rom", "See available rooms")} <ArrowRight className="h-4 w-4" /></Link></Button>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
